import { ImageScaleView, View } from '@play-co/timestep-core/ui';
import { applyRelativeOffset, getSceneManager } from 'src/lib/utils';
import { captureGenericError } from 'src/lib/sentry';
import type { CardPackTier } from 'src/replicant/cardpack/cardpack.ruleset';
import gsap from 'gsap';
import { ViewPosition } from '@play-co/timestep-core/lib/ui/View';
import { CardsButton } from './components/CardsButton';
import { cardsOpenPackByTier } from './cards.sequence';
import { CARDS_REWARD_ICON_PATH, cardsGetPackIcon } from './cards.assets';

export async function cardsAnimateRewardIconToHomeButton(args: {
  superview: View;
  position: ViewPosition;
}) {
  await animateImageToCardsHomeButton({
    superview: args.superview,
    position: args.position,
    image: CARDS_REWARD_ICON_PATH,
    iconSize: 110,
  });
}

async function animateImageToCardsHomeButton(args: {
  superview: View;
  position: ViewPosition;
  image: string;
  iconSize: number;
}) {
  // Resolve the current Cards HUD button as the animation destination.
  const homeButton = getSceneManager()?.currentScene?.getIcon('cards') as
    | CardsButton
    | undefined;
  if (!homeButton) {
    captureGenericError(
      'Cannot play cards animation: home button is not available.',
      null,
    );
    return;
  }

  const duration = 1;
  const superview = args.superview;
  const parent = homeButton.getSuperview()!;
  const homeButtonPosition = homeButton.getPosition(parent);
  applyRelativeOffset(homeButtonPosition, parent, superview);

  const fromPosition = args.position;
  const toPosition = {
    x: homeButtonPosition.x + homeButtonPosition.width / 2,
    y: homeButtonPosition.y + homeButtonPosition.height / 2,
    width: homeButtonPosition.width,
    height: homeButtonPosition.height,
  };

  const iconClone = new ImageScaleView({
    superview: superview,
    image: args.image,
    x: fromPosition.x,
    y: fromPosition.y,
    width: args.iconSize,
    height: args.iconSize,
    scale: fromPosition.width / args.iconSize,
    zIndex: 9000,
    scaleMethod: 'contain',
    centerOnOrigin: true,
  });

  gsap.to(iconClone.style, {
    scale: 1,
    duration,
    ease: 'back.out',
  });

  gsap.to(iconClone.style, {
    x: toPosition.x,
    duration,
    ease: 'quart.inOut',
  });

  await gsap.to(iconClone.style, {
    y: toPosition.y,
    duration,
    ease: 'sine.inOut',
  });
  iconClone.removeFromSuperview();

  // Play the real button's collection reaction when the reward lands.
  void homeButton.playHitAnimation();

  await cardsPlayShineExplosion({
    superview,
    x: toPosition.x,
    y: toPosition.y,
  });
}

export async function cardsAnimatePackOpenFromIcon(args: {
  superview: View;
  position: ViewPosition;
  tier: CardPackTier;
}) {
  const iconClone = new ImageScaleView({
    superview: args.superview,
    image: cardsGetPackIcon(args.tier),
    x: args.position.x,
    y: args.position.y,
    width: args.position.width,
    height: args.position.height,
    zIndex: 9000,
    scale: 0.9,
    scaleMethod: 'contain',
    centerOnOrigin: true,
  });

  gsap
    .to(iconClone.style, {
      scale: 2,
      opacity: 0,
      duration: 0.2,
      ease: 'linear',
    })
    .then(() => iconClone.removeFromSuperview());

  await cardsOpenPackByTier(args.tier);
}

/**
 * Play a shine explosion effect
 * @param args
 */
async function cardsPlayShineExplosion(args: {
  superview: View;
  x: number;
  y: number;
  numParticles?: number;
  distance?: number;
  duration?: number;
}) {
  const numParticles = args.numParticles ?? 8;
  const distance = args.distance ?? 100;
  const duration = args.duration ?? 0.5;
  const slice = (Math.PI * 2) / numParticles;
  const promises = [];

  for (let i = 0; i < numParticles; i++) {
    const particle = new ImageScaleView({
      superview: args.superview,
      image: 'assets/ui/match2/common/shine_particle.png',
      x: args.x,
      y: args.y,
      width: 27,
      height: 27,
      zIndex: 9001,
      centerOnOrigin: true,
      scale: 3,
    });

    promises.push(
      gsap
        .to(particle.style, {
          x: args.x + Math.cos(slice * i) * distance,
          y: args.y + Math.sin(slice * i) * distance,
          scale: 1,
          duration,
          ease: 'quad.out',
        })
        .then(() => particle.removeFromSuperview()),
    );

    promises.push(
      gsap
        .to(particle.style, {
          opacity: 0,
          duration: duration * 0.4,
          delay: duration * 0.6,
          ease: 'linear',
        })
        .then(() => particle.removeFromSuperview()),
    );
  }

  await Promise.all(promises);
}
